import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, AlertCircle, X, Mail, KeyRound, RefreshCw } from 'lucide-react';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // Persistent error state
  const { login } = useAuth();
  const navigate = useNavigate();

  const clearError = () => setError(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    // Validation with specific error messages
    if (!email && !password) {
      setError({
        type: 'empty_fields',
        message: 'Please enter your email and password',
        action: 'Fill in both fields below to continue'
      });
      return;
    }

    if (!email) {
      setError({
        type: 'missing_email',
        message: 'Email address is required',
        action: 'Enter your email address to continue'
      });
      return;
    }

    if (!password) {
      setError({
        type: 'missing_password',
        message: 'Password is required',
        action: 'Enter your password to continue'
      });
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError({
        type: 'invalid_email',
        message: 'Please enter a valid email address',
        action: 'Check your email format (e.g., you@example.com)'
      });
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/events');
    } catch (error) {
      console.error('Login error:', error);
      console.log('Setting error state...');
      const status = error.response?.status;
      const detail = error.response?.data?.detail || '';
      console.log('Status:', status, 'Detail:', detail);

      // Map different error scenarios to helpful messages
      if (status === 403 || detail.toLowerCase().includes('revoked')) {
        setError({
          type: 'access_revoked',
          message: 'Your account access has been revoked',
          action: 'Please contact support for assistance',
          showSupport: true
        });
      } else if (status === 401 || detail.toLowerCase().includes('invalid')) {
        setError({
          type: 'invalid_credentials',
          message: 'Invalid email or password',
          action: 'Double-check your credentials or reset your password',
          showForgotPassword: true
        });
      } else if (status === 404 || detail.toLowerCase().includes('not found')) {
        setError({
          type: 'user_not_found',
          message: 'No account found with this email',
          action: 'Check your email or create a new account',
          showSignup: true
        });
      } else if (status === 429) {
        setError({
          type: 'rate_limited',
          message: 'Too many login attempts',
          action: 'Please wait a few minutes before trying again',
          showRetry: true
        });
      } else if (status >= 500) {
        setError({
          type: 'server_error',
          message: 'Server error. Please try again later.',
          action: 'Our servers are temporarily unavailable',
          showRetry: true
        });
      } else if (!navigator.onLine) {
        setError({
          type: 'network_error',
          message: 'No internet connection',
          action: 'Check your network connection and try again',
          showRetry: true
        });
      } else {
        setError({
          type: 'unknown',
          message: detail || 'Login failed',
          action: 'Please try again or contact support',
          showRetry: true
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    clearError();
    // Focus the appropriate field based on error type
    if (error?.type === 'missing_email' || error?.type === 'invalid_email') {
      document.getElementById('email')?.focus();
    } else {
      document.getElementById('password')?.focus();
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-8 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
          <div className="mb-8">
            <h1 className="text-3xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">Welcome back</h1>
            <p className="text-[#4A4A4A]">Log in to continue finding vendor events</p>
          </div>

          {/* Persistent Error Banner */}
          {error && (
            <div 
              className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl animate-shake"
              data-testid="login-error-banner"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-red-800" data-testid="login-error-message">
                    {error.message}
                  </p>
                  <p className="text-sm text-red-600 mt-1">
                    {error.action}
                  </p>
                  
                  {/* Contextual action buttons */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {error.showForgotPassword && (
                      <Link to="/forgot-password">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-700 border-red-300 hover:bg-red-100"
                          data-testid="error-forgot-password-btn"
                        >
                          <KeyRound className="w-3 h-3 mr-1" />
                          Reset Password
                        </Button>
                      </Link>
                    )}
                    {error.showSignup && (
                      <Link to="/signup">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-700 border-red-300 hover:bg-red-100"
                          data-testid="error-signup-btn"
                        >
                          <Mail className="w-3 h-3 mr-1" />
                          Create Account
                        </Button>
                      </Link>
                    )}
                    {error.showRetry && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-700 border-red-300 hover:bg-red-100"
                        onClick={handleRetry}
                        data-testid="error-retry-btn"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Try Again
                      </Button>
                    )}
                    {error.showSupport && (
                      <a href="mailto:support@amscompass.com">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-700 border-red-300 hover:bg-red-100"
                          data-testid="error-support-btn"
                        >
                          <Mail className="w-3 h-3 mr-1" />
                          Contact Support
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
                <button 
                  onClick={clearError}
                  className="text-red-400 hover:text-red-600 p-1"
                  aria-label="Dismiss error"
                  data-testid="error-dismiss-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-medium text-[#1E3A5F]">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`input-default w-full ${error?.type === 'missing_email' || error?.type === 'invalid_email' || error?.type === 'user_not_found' ? 'border-red-300 focus:border-red-500' : ''}`}
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="font-medium text-[#1E3A5F]">Password</Label>
                <Link to="/forgot-password" className="text-sm text-[#FE5F55] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`input-default w-full ${error?.type === 'missing_password' || error?.type === 'invalid_credentials' ? 'border-red-300 focus:border-red-500' : ''}`}
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Log in'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#4A4A4A]">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#FE5F55] font-semibold hover:underline">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Shake animation for error banner */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default Login;
